import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Application } from "../../types";
import {
  TELANGANA_DISTRICTS,
  telanganaDistrictKeyFromStored,
  type TelanganaDistrictKey,
} from "../../data/telanganaDistricts";
import {
  DISTRICT_FILL_PALETTE,
  TELANGANA_GOV_HOTSPOTS,
  TELANGANA_GOV_MAP_HEIGHT,
  TELANGANA_GOV_MAP_WIDTH,
} from "../../data/telanganaGovMap";
import {
  mandalHotspotsForDistrict,
  mandalLabelLayout,
  mandalViewBoxForDistrict,
} from "../../data/telanganaMandalMap";
import { useGeoDirectory } from "../../context/GeoDirectoryContext";

function aggregateByDistrict(apps: Application[]) {
  const applicants = new Map<TelanganaDistrictKey, number>();
  const beneficiaries = new Map<TelanganaDistrictKey, number>();
  for (const d of TELANGANA_DISTRICTS) {
    applicants.set(d.key, 0);
    beneficiaries.set(d.key, 0);
  }
  for (const a of apps) {
    const dk = telanganaDistrictKeyFromStored(a.district);
    if (!dk) continue;
    applicants.set(dk, (applicants.get(dk) ?? 0) + 1);
    if (a.status === "subcontractor_linked") {
      beneficiaries.set(dk, (beneficiaries.get(dk) ?? 0) + 1);
    }
  }
  return { applicants, beneficiaries };
}

function labelForKey(key: TelanganaDistrictKey): string {
  return TELANGANA_DISTRICTS.find((d) => d.key === key)?.label ?? key;
}

function labelLinesForKey(key: TelanganaDistrictKey): string[] {
  const meta = TELANGANA_DISTRICTS.find((d) => d.key === key);
  if (meta && meta.lines.length > 1) return [...meta.lines];
  return [labelForKey(key)];
}

export function TelanganaDistrictMap({
  applications,
  className,
}: {
  applications: Application[];
  className?: string;
}) {
  const { store: geoStore, loading: geoLoading, error: geoError } = useGeoDirectory();
  const [active, setActive] = useState<TelanganaDistrictKey | null>(null);
  const [drilledDistrict, setDrilledDistrict] = useState<TelanganaDistrictKey | null>(null);
  const [pointerClient, setPointerClient] = useState<{ x: number; y: number } | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const { applicants, beneficiaries } = useMemo(() => aggregateByDistrict(applications), [applications]);

  const activeMeta = active ? TELANGANA_DISTRICTS.find((d) => d.key === active) : null;
  const aCount = active ? (applicants.get(active) ?? 0) : 0;
  const bCount = active ? (beneficiaries.get(active) ?? 0) : 0;

  const statewide = useMemo(() => {
    let totalApplicants = 0;
    let totalBeneficiaries = 0;
    for (const d of TELANGANA_DISTRICTS) {
      const a = applicants.get(d.key) ?? 0;
      const b = beneficiaries.get(d.key) ?? 0;
      totalApplicants += a;
      totalBeneficiaries += b;
    }
    return { totalApplicants, totalBeneficiaries };
  }, [applicants, beneficiaries]);

  const directoryTotals = useMemo(
    () => (geoStore ? geoStore.tgDirectoryAggregateCounts() : null),
    [geoStore],
  );

  const activeDirectory = useMemo(
    () => (active && geoStore ? geoStore.tgDirectoryCountsForDistrict(active) : null),
    [active, geoStore],
  );

  const tooltipPosition = useMemo(() => {
    if (!pointerClient || !frameRef.current) return null;
    const r = frameRef.current.getBoundingClientRect();
    const pad = 12;
    const tw = 252;
    const th = 210;
    let left = pointerClient.x - r.left + pad;
    let top = pointerClient.y - r.top + pad;
    left = Math.max(8, Math.min(left, r.width - tw - 8));
    top = Math.max(8, Math.min(top, r.height - th - 8));
    return { left, top };
  }, [pointerClient, active]);

  const sectionClass = ["dash-corp-map", className].filter(Boolean).join(" ");

  const fillByIndex = useMemo(() => {
    const m = new Map<TelanganaDistrictKey, string>();
    TELANGANA_GOV_HOTSPOTS.forEach((row, i) => {
      m.set(row.key, DISTRICT_FILL_PALETTE[i % DISTRICT_FILL_PALETTE.length]);
    });
    return m;
  }, []);

  const drilledMeta = drilledDistrict
    ? TELANGANA_DISTRICTS.find((d) => d.key === drilledDistrict)
    : null;
  const mandalsForDrill = useMemo(
    () => (drilledDistrict ? mandalHotspotsForDistrict(drilledDistrict) : null),
    [drilledDistrict],
  );
  const isMandalView = Boolean(drilledDistrict && mandalsForDrill && mandalsForDrill.length > 0);
  const mandalViewBox = useMemo(
    () => (drilledDistrict ? mandalViewBoxForDistrict(drilledDistrict) : null),
    [drilledDistrict],
  );
  const svgViewBox = isMandalView && mandalViewBox
    ? mandalViewBox
    : `0 0 ${TELANGANA_GOV_MAP_WIDTH} ${TELANGANA_GOV_MAP_HEIGHT}`;
  const viewBoxParts = useMemo(() => {
    const [x, y, w, h] = svgViewBox.split(/\s+/).map(Number);
    return { x, y, w, h };
  }, [svgViewBox]);

  /** Match state-map border weight when zoomed to mandal viewBox. */
  const mapStrokeScale = viewBoxParts.w / TELANGANA_GOV_MAP_WIDTH;
  const pathStrokeWidth = 1.15 * mapStrokeScale;
  const pathStrokeWidthActive = 2.75 * mapStrokeScale;
  const mandalLabelMaxFont = viewBoxParts.w * (13 / TELANGANA_GOV_MAP_WIDTH);
  const mandalLabelMinFont = Math.max(1.8, viewBoxParts.w * (5 / TELANGANA_GOV_MAP_WIDTH));

  const [labelCenters, setLabelCenters] = useState<Record<string, { x: number; y: number }>>({});
  const [mandalLabelCenters, setMandalLabelCenters] = useState<
    { title: string; x: number; y: number; width: number; height: number; index: number }[]
  >([]);

  useLayoutEffect(() => {
    const root = frameRef.current;
    if (!root) return;
    const svg = root.querySelector("svg");
    if (!svg) return;
    if (isMandalView) {
      const nodes = svg.querySelectorAll<SVGGraphicsElement>("[data-mandal-label-id]");
      const next: { title: string; x: number; y: number; width: number; height: number; index: number }[] = [];
      nodes.forEach((el) => {
        const title = el.getAttribute("data-mandal-title")?.trim() ?? "";
        const index = Number(el.getAttribute("data-mandal-label-id"));
        if (!title || Number.isNaN(index)) return;
        const b = el.getBBox();
        next.push({
          title,
          index,
          x: b.x + b.width / 2,
          y: b.y + b.height / 2,
          width: b.width,
          height: b.height,
        });
      });
      setMandalLabelCenters(next);
      return;
    }
    setMandalLabelCenters([]);
    const next: Record<string, { x: number; y: number }> = {};
    for (const row of TELANGANA_GOV_HOTSPOTS) {
      const el = svg.querySelector(`#tg-path-${row.key}`);
      if (!el) continue;
      const b = (el as SVGGraphicsElement).getBBox();
      next[row.key] = { x: b.x + b.width / 2, y: b.y + b.height / 2 };
    }
    setLabelCenters(next);
  }, [isMandalView, drilledDistrict, svgViewBox, mandalsForDrill]);

  return (
    <section className={sectionClass} aria-labelledby="dash-corp-map-heading">
      <div className="dash-corp-map__head">
        <h1 id="dash-corp-map-heading" className="dash-corp-map__title">
          {drilledMeta
            ? `Telangana — ${drilledMeta.label} (mandal view)`
            : "Telangana — district view"}
        </h1>
        {drilledMeta ? (
          <p className="dash-corp-map__subtitle text-muted">
            {mandalsForDrill?.length ?? 0} mandals with names — hover a subdivision for details.
          </p>
        ) : (
          <p className="dash-corp-map__subtitle text-muted">Click a district to zoom in and show mandal boundaries.</p>
        )}
      </div>

      <div className="dash-corp-map__layout">
        <figure className="dash-corp-map__figure">
          <div
            ref={frameRef}
            className="dash-corp-map__frame dash-corp-map__frame--geo"
            onMouseLeave={() => {
              setActive(null);
              setPointerClient(null);
            }}
          >
            <div className="dash-corp-map__geo-clip" style={{ position: 'relative' }}>
              {drilledDistrict && (
                <button
                  className="btn btn--sm"
                  style={{
                    position: 'absolute', top: 16, left: 16, zIndex: 10,
                    background: 'var(--surface-solid)', color: 'var(--text)',
                    border: '1px solid var(--border-strong)', borderRadius: 4,
                    padding: '6px 12px', cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onClick={() => {
                    setDrilledDistrict(null);
                    setActive(null);
                  }}
                >
                  ← Back to State View
                </button>
              )}
            <svg
              className="dash-corp-map__geo"
              viewBox={svgViewBox}
              role="img"
              aria-label={
                isMandalView
                  ? `Telangana ${drilledMeta?.label ?? ""} mandal map`
                  : "Telangana district map with selectable boundaries"
              }
            >
              <rect
                className="tg-geo-backdrop"
                x={viewBoxParts.x}
                y={viewBoxParts.y}
                width={viewBoxParts.w}
                height={viewBoxParts.h}
              />
              {!isMandalView ? (
              <g className="tg-geo-districts">
              {TELANGANA_GOV_HOTSPOTS.map((row) => {
                const isOn = active === row.key;
                const fill = fillByIndex.get(row.key) ?? "#e2e8f0";
                const ac = applicants.get(row.key) ?? 0;
                const bc = beneficiaries.get(row.key) ?? 0;
                return (
                  <path
                    key={row.key}
                    id={`tg-path-${row.key}`}
                    d={row.d}
                    className={isOn ? "tg-geo-path tg-geo-path--active" : "tg-geo-path"}
                    fill={fill}
                    fillOpacity={1}
                    stroke="var(--tg-map-stroke, #1e293b)"
                    strokeWidth={isOn ? pathStrokeWidthActive : pathStrokeWidth}
                    strokeLinejoin="round"
                    tabIndex={0}
                    role="button"
                    aria-label={`${row.title}. ${ac} applicants, ${bc} beneficiaries with subcontractor linked.`}
                    onMouseEnter={(e) => {
                      setActive(row.key);
                      setPointerClient({ x: e.clientX, y: e.clientY });
                    }}
                    onClick={() => {
                      setDrilledDistrict(row.key);
                      setActive(null);
                      setPointerClient(null);
                    }}
                    onFocus={() => {
                      setActive(row.key);
                      const fr = frameRef.current?.getBoundingClientRect();
                      if (fr) {
                        setPointerClient({
                          x: fr.left + fr.width * 0.42,
                          y: fr.top + fr.height * 0.38,
                        });
                      }
                    }}
                    onBlur={(e) => {
                      const next = e.relatedTarget as Node | null;
                      if (frameRef.current && next && frameRef.current.contains(next)) return;
                      setActive(null);
                      setPointerClient(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActive(row.key);
                      }
                      if (e.key === "Escape") {
                        setActive(null);
                        setPointerClient(null);
                      }
                    }}
                  >
                    <title>{row.title}</title>
                  </path>
                );
              })}
              </g>
              ) : null}

              {isMandalView ? (
              <defs>
                {mandalsForDrill!.map((m, i) => (
                  <clipPath key={`clip-${i}`} id={`tg-mandal-clip-${i}`} clipPathUnits="userSpaceOnUse">
                    <path d={m.d} />
                  </clipPath>
                ))}
              </defs>
              ) : null}

              {isMandalView ? (
              <g className="tg-geo-mandals">
                {mandalsForDrill!.map((m, i) => (
                    <path
                      key={`${m.title}-${i}`}
                      id={`tg-mandal-path-${i}`}
                      data-mandal-label-id={i}
                      data-mandal-title={m.title}
                      d={m.d}
                      className="tg-geo-path tg-geo-path--mandal"
                      fill={DISTRICT_FILL_PALETTE[i % DISTRICT_FILL_PALETTE.length]}
                      fillOpacity={1}
                      stroke="var(--tg-map-stroke, #1e293b)"
                      strokeWidth={pathStrokeWidth}
                      strokeLinejoin="round"
                      style={{ outline: "none" }}
                    >
                      <title>{m.title}</title>
                    </path>
                  ))}
              </g>
              ) : null}

              {isMandalView ? (
              <g className="tg-geo-labels tg-geo-labels--mandals" pointerEvents="none" aria-hidden="true">
                {mandalLabelCenters.map((row) => {
                  const layout = mandalLabelLayout(row.title, row.width, row.height, {
                    maxFontSize: mandalLabelMaxFont,
                    minFontSize: mandalLabelMinFont,
                  });
                  if (layout.hide || !layout.lines.length) return null;
                  const { lines, fontSize } = layout;
                  const lineGap = fontSize * 1.12;
                  const startY = row.y - ((lines.length - 1) * lineGap) / 2;
                  const labelStroke = Math.max(0.08, fontSize * 0.1);
                  return (
                    <text
                      key={`${row.index}-${row.title}`}
                      x={row.x}
                      y={startY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={fontSize}
                      clipPath={`url(#tg-mandal-clip-${row.index})`}
                      className="tg-geo-label tg-geo-label--mandal"
                      strokeWidth={labelStroke}
                    >
                      {lines.map((line, li) => (
                        <tspan key={li} x={row.x} dy={li === 0 ? 0 : lineGap}>
                          {line}
                        </tspan>
                      ))}
                    </text>
                  );
                })}
              </g>
              ) : null}

              {!isMandalView ? (
              <g className="tg-geo-labels" pointerEvents="none" aria-hidden="true">
                {TELANGANA_GOV_HOTSPOTS.map((row) => {
                  const c = labelCenters[row.key];
                  if (!c) return null;
                  const lines = labelLinesForKey(row.key);
                  const long = lines.some((l) => l.length > 14) || lines.length > 1;
                  const fontSize = long ? 19 : 24;
                  const lineGap = fontSize * 1.05;
                  const startY = c.y - ((lines.length - 1) * lineGap) / 2;
                  return (
                    <text
                      key={row.key}
                      x={c.x}
                      y={startY}
                      textAnchor="middle"
                      fontSize={fontSize}
                      className="tg-geo-label"
                    >
                      {lines.map((line, i) => (
                        <tspan key={i} x={c.x} dy={i === 0 ? 0 : lineGap}>
                          {line}
                        </tspan>
                      ))}
                    </text>
                  );
                })}
              </g>
              ) : null}
            </svg>
            </div>
            {active && activeMeta && tooltipPosition ? (
              <div
                className="dash-corp-map__map-tooltip"
                style={{ left: tooltipPosition.left, top: tooltipPosition.top }}
                role="tooltip"
              >
                <p className="dash-corp-map__map-tooltip__eyebrow">District</p>
                <p className="dash-corp-map__map-tooltip__title">{activeMeta.label}</p>
                <dl className="dash-corp-map__map-tooltip__stats">
                  <div>
                    <dt>Applicants</dt>
                    <dd>{aCount}</dd>
                  </div>
                  <div>
                    <dt>Beneficiaries</dt>
                    <dd>{bCount}</dd>
                  </div>
                </dl>
                <p className="dash-corp-map__map-tooltip__hint text-muted">
                  Beneficiaries = <strong>Subcontractor linked</strong> in this district.
                </p>
              </div>
            ) : null}
          </div>
        </figure>

        <aside className="dash-corp-map__aside" aria-label="Map context and figures">
          <div className="dash-corp-map__side-card">
            <p className="dash-corp-map__side-eyebrow">Statewide snapshot</p>
            <p className="dash-corp-map__side-lead">Counts across all beneficiary files in this console.</p>
            <dl className="dash-corp-map__side-stats">
              <div>
                <dt>Total applicants</dt>
                <dd>{statewide.totalApplicants}</dd>
              </div>
              <div>
                <dt>Total beneficiaries</dt>
                <dd>{statewide.totalBeneficiaries}</dd>
              </div>
            </dl>
          </div>

          <div className="dash-corp-map__side-card">
            <p className="dash-corp-map__side-eyebrow">Registration directory</p>
            <dl className="dash-corp-map__side-stats dash-corp-map__side-stats--compact">
              <div>
                <dt>Districts</dt>
                <dd>
                  {geoLoading || directoryTotals == null
                    ? "—"
                    : directoryTotals.districts.toLocaleString("en-IN")}
                </dd>
              </div>
              <div>
                <dt>Mandals</dt>
                <dd>
                  {geoLoading || directoryTotals == null
                    ? "—"
                    : directoryTotals.mandals.toLocaleString("en-IN")}
                </dd>
              </div>
              <div>
                <dt>Villages</dt>
                <dd>
                  {geoLoading || directoryTotals == null
                    ? "—"
                    : directoryTotals.villages.toLocaleString("en-IN")}
                </dd>
              </div>
            </dl>
            {!geoLoading && !geoStore && geoError ? (
              <p className="text-muted" style={{ margin: "0.5rem 0 0", fontSize: "0.8125rem", lineHeight: 1.45 }}>
                Directory did not load ({geoError}). Ensure the API is running and MySQL geo tables are seeded; the client
                will retry on refresh.
              </p>
            ) : null}
            {!geoLoading && geoStore && directoryTotals && directoryTotals.districts === 0 ? (
              <p className="text-muted" style={{ margin: "0.5rem 0 0", fontSize: "0.8125rem", lineHeight: 1.45 }}>
                Registration payload had no districts. Check <code>/api/geo/directory</code> response shape.
              </p>
            ) : null}
          </div>

          {active && activeMeta ? (
            <div className="dash-corp-map__side-card dash-corp-map__side-card--district">
              <p className="dash-corp-map__side-eyebrow">Selected district</p>
              <p className="dash-corp-map__side-title">{activeMeta.label}</p>
              <dl className="dash-corp-map__side-stats dash-corp-map__side-stats--compact">
                <div>
                  <dt>Applicants</dt>
                  <dd>{aCount}</dd>
                </div>
                <div>
                  <dt>Linked beneficiaries</dt>
                  <dd>{bCount}</dd>
                </div>
                {activeDirectory ? (
                  <>
                    <div>
                      <dt>Mandals (dir.)</dt>
                      <dd>{activeDirectory.mandals}</dd>
                    </div>
                    <div>
                      <dt>Villages (dir.)</dt>
                      <dd>{activeDirectory.villages.toLocaleString()}</dd>
                    </div>
                  </>
                ) : null}
              </dl>
              <Link
                to={`/reports?district=${active}`}
                className="btn btn--primary btn--tg btn--sm dash-corp-map__side-link"
              >
                Open reports for this district
              </Link>
            </div>
          ) : (
            <div className="dash-corp-map__side-card dash-corp-map__side-card--muted">
              <p className="dash-corp-map__side-eyebrow">District focus</p>
              <p className="dash-corp-map__side-placeholder">
                Hover or focus a district on the map to see its counts and a quick link to mandal-level area reports.
              </p>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
